// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert,
  Typography, Stack, MenuItem, Box, IconButton, Chip, Tooltip, LinearProgress,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';

// In-app feedback, reachable from every screen by everyone — the people who
// hit a broken screen are mostly not the people who administer it.
//
// The context is CAPTURED rather than asked for. Without it the first reply is
// always "which screen, which client, which browser", by which point the person
// has moved on and the report is worthless.

const KINDS = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'idea', label: 'Idea or request' },
  { value: 'question', label: 'Question' },
];
const SEVERITIES = [
  { value: 'blocking', label: 'Blocking — I cannot continue' },
  { value: 'annoying', label: 'Annoying — I have a workaround' },
  { value: 'minor', label: 'Minor — worth noting' },
];

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

// Screen capture is the browser's own API, not a DOM rasteriser. html2canvas and
// friends re-render the page and quietly disagree with it — wrong fonts, missing
// MUI overlays, absent canvas content — which produces a screenshot that is
// evidence of nothing. getDisplayMedia captures what is actually on the glass,
// and the browser's own picker is the consent step: we never choose what is
// shared, the person does.
const canCaptureScreen = () => !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);

async function captureScreenshot() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
  });
  try {
    const track = stream.getVideoTracks()[0];
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // One painted frame has to land before the canvas has anything to copy;
    // grabbing immediately yields a blank image on every browser tested.
    await new Promise((resolve) => {
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(() => resolve());
      else setTimeout(resolve, 250);
    });
    const settings = track.getSettings ? track.getSettings() : {};
    const canvas = document.createElement('canvas');
    canvas.width = settings.width || video.videoWidth;
    canvas.height = settings.height || video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    video.pause();
    video.srcObject = null;
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } finally {
    // Always, on every path — a live capture track leaves the browser showing
    // "sharing your screen" indefinitely, which is alarming and looks like a bug.
    stream.getTracks().forEach((t) => t.stop());
  }
}

export default function Feedback({ open, onClose, screen, workspace }) {
  const [kind, setKind] = useState('bug');
  const [severity, setSeverity] = useState('annoying');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]); // { blob, name, url, contentType }
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileInput = useRef(null);

  const session = getSession();

  const reset = () => {
    setKind('bug'); setSeverity('annoying'); setTitle(''); setBody('');
    setFiles((prev) => { prev.forEach((f) => URL.revokeObjectURL(f.url)); return []; });
    setErr(''); setDone(false);
  };

  const close = () => { reset(); onClose(); };

  const addBlob = useCallback((blob, name) => {
    if (!blob) return;
    if (blob.size > MAX_ATTACHMENT_BYTES) {
      setErr(`"${name}" is larger than 10 MB and was not attached.`);
      return;
    }
    setFiles((prev) => [...prev, {
      blob, name, url: URL.createObjectURL(blob), contentType: blob.type || 'application/octet-stream',
    }]);
  }, []);

  // Paste straight from the clipboard, so the OS screenshot people already know
  // how to take (Cmd-Shift-4 / Win-Shift-S) works without them saving a file
  // first and hunting for it in a picker.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
      const image = items.find((i) => i.type && i.type.startsWith('image/'));
      if (!image) return;
      e.preventDefault();
      addBlob(image.getAsFile(), `pasted-${items.indexOf(image) + 1}.png`);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, addBlob]);

  // Revoke on unmount as well as on removal — these are object URLs holding
  // whole screenshots in memory.
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.url)), [files]);

  const removeFile = (i) => setFiles((prev) => {
    URL.revokeObjectURL(prev[i].url);
    return prev.filter((_, n) => n !== i);
  });

  const grabScreen = async () => {
    setErr('');
    try {
      addBlob(await captureScreenshot(), `screenshot-${files.length + 1}.png`);
    } catch (e) {
      // Cancelling the browser's picker throws NotAllowedError. That is a
      // choice, not a failure, and must not be reported as one.
      if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) return;
      setErr('This browser would not capture the screen. You can still paste an image or attach a file.');
    }
  };

  const api = async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      // Attachments first. If one fails the report still goes, minus that file —
      // losing a written report because an upload failed is the worst outcome
      // here, since the person will not write it twice.
      const attachments = [];
      const failed = [];
      for (const f of files) {
        try {
          const presign = await api('/documents/presign', {
            method: 'POST',
            body: JSON.stringify({ fileName: f.name, contentType: f.contentType, scope: 'client' }),
          });
          const putUrl = presign.uploadUrl || presign.url;
          if (!putUrl) throw new Error('no upload URL');
          const put = await fetch(putUrl, {
            method: 'PUT',
            headers: { 'Content-Type': f.contentType },
            body: f.blob,
          });
          if (!put.ok) throw new Error(`upload failed (${put.status})`);
          attachments.push({ key: presign.s3Key || presign.key, name: f.name, contentType: f.contentType });
        } catch (e2) {
          failed.push(f.name);
        }
      }

      await api('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          kind,
          severity,
          title,
          body,
          screen: screen || null,
          workspace: workspace || null,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          attachments,
        }),
      });
      setFiles((prev) => { prev.forEach((f) => URL.revokeObjectURL(f.url)); return []; });
      setErr(failed.length ? `Sent, but these could not be attached: ${failed.join(', ')}.` : '');
      setDone(true);
    } catch (e2) {
      setErr(e2.message || 'Could not send your feedback.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && close()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>Send feedback</DialogTitle>
      <form onSubmit={submit}>
        <DialogContent dividers>
          {done ? (
            <Alert severity="success">
              Thank you — this has been logged with the screen and workspace you were on, so it can
              be reproduced without going back to you first.
            </Alert>
          ) : (
            <>
              {err && <Alert severity="warning" sx={{ mb: 2 }}>{err}</Alert>}

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField select fullWidth size="small" label="What is this?" value={kind}
                  onChange={(e) => setKind(e.target.value)}>
                  {KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
                </TextField>
                <TextField select fullWidth size="small" label="How bad?" value={severity}
                  onChange={(e) => setSeverity(e.target.value)}>
                  {SEVERITIES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              </Stack>

              <TextField fullWidth size="small" label="One-line summary" required sx={{ mb: 2 }}
                value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />

              <TextField fullWidth size="small" multiline minRows={4} sx={{ mb: 2 }}
                label="What happened?" value={body} onChange={(e) => setBody(e.target.value)}
                helperText="What you were doing, what you expected, and what happened instead." />

              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />}
                  onClick={grabScreen} disabled={busy || !canCaptureScreen()}
                  sx={{ color: '#0D5B9A', borderColor: '#0D5B9A', textTransform: 'none' }}>
                  Capture screen
                </Button>
                <Button size="small" variant="outlined" startIcon={<AttachFileIcon />}
                  onClick={() => fileInput.current && fileInput.current.click()} disabled={busy}
                  sx={{ color: '#0D5B9A', borderColor: '#0D5B9A', textTransform: 'none' }}>
                  Attach a file
                </Button>
                <input ref={fileInput} type="file" multiple hidden
                  onChange={(e) => {
                    Array.from(e.target.files || []).forEach((f) => addBlob(f, f.name));
                    e.target.value = '';
                  }} />
              </Stack>
              <Typography sx={{ fontSize: 11.5, color: '#9AA6B2', mb: 1.5 }}>
                {canCaptureScreen()
                  ? 'Your browser will ask which window to share, and only that is captured. You can also paste an image straight into this dialog.'
                  : 'This browser cannot capture the screen from a page. Take one with Cmd-Shift-4 or Win-Shift-S and paste it here.'}
              </Typography>

              {!!files.length && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {files.map((f, i) => (
                    <Stack key={f.url} direction="row" alignItems="center" spacing={1}
                      sx={{ p: 0.75, border: '1px solid #E2E7EC', borderRadius: 1 }}>
                      {f.contentType.startsWith('image/') && (
                        <Box component="img" src={f.url} alt=""
                          sx={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 0.5, border: '1px solid #E2E7EC' }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#9AA6B2' }}>
                          {(f.blob.size / 1024).toFixed(0)}
                          {' KB'}
                        </Typography>
                      </Box>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeFile(i)} disabled={busy}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Box sx={{ mt: 2, p: 1.25, bgcolor: '#F4F7FA', border: '1px solid #E2E7EC', borderRadius: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5C6B7A', letterSpacing: 0.6, mb: 0.5 }}>
                  SENT WITH THIS REPORT
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={`screen: ${screen || 'unknown'}`} sx={{ height: 20, fontSize: 11 }} />
                  <Chip size="small" variant="outlined" label={`workspace: ${workspace || 'none'}`} sx={{ height: 20, fontSize: 11 }} />
                  <Chip size="small" variant="outlined" label={session ? session.email : 'not signed in'} sx={{ height: 20, fontSize: 11 }} />
                  <Chip size="small" variant="outlined" label={`${window.innerWidth}x${window.innerHeight}`} sx={{ height: 20, fontSize: 11 }} />
                </Stack>
              </Box>

              {busy && <LinearProgress sx={{ mt: 2 }} />}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>{done ? 'Close' : 'Cancel'}</Button>
          {!done && (
            <Button type="submit" variant="contained" disabled={busy || !title.trim()}
              sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
              {busy ? 'Sending…' : 'Send feedback'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
